import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  ArrowUp,
  ArrowDown,
  GripVertical,
  Save,
  X,
  RotateCcw
} from 'lucide-react';
import api from '../services/api';

const HotelOrderModal = ({ open, onClose, onSave }) => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (open) {
      fetchHotels();
    }
  }, [open]);

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const response = await api.get('/hotels/order');
      setHotels(response.data);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      showSnackbar('Error loading hotels', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newHotels = [...hotels];
    [newHotels[index - 1], newHotels[index]] = [newHotels[index], newHotels[index - 1]];
    setHotels(newHotels);
  };

  const moveDown = (index) => {
    if (index === hotels.length - 1) return;
    const newHotels = [...hotels];
    [newHotels[index], newHotels[index + 1]] = [newHotels[index + 1], newHotels[index]];
    setHotels(newHotels);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.parentNode);
    e.dataTransfer.setDragImage(e.target.parentNode, 0, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) {
      return;
    }

    const newHotels = [...hotels];
    const draggedHotel = newHotels[draggedItem];
    
    // Remove o item arrastado
    newHotels.splice(draggedItem, 1);
    
    // Insere na nova posição
    const insertIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
    newHotels.splice(insertIndex, 0, draggedHotel);
    
    setHotels(newHotels);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const hotelOrders = hotels.map((hotel, index) => ({
        hotel_id: hotel.id,
        display_order: index + 1
      }));

      await api.put('/hotels/order', { hotel_orders: hotelOrders });
      showSnackbar('Hotel order saved successfully!');
      onSave();
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Error saving order:', error);
      showSnackbar('Error saving hotel order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const confirmReset = window.confirm(
      "Are you sure you want to reset the hotel order to alphabetical order?"
    );
    
    if (!confirmReset) return;

    setSaving(true);
    try {
      await api.delete('/hotels/order/reset');
      showSnackbar('Hotel order reset!');
      fetchHotels();
    } catch (error) {
      console.error('Error resetting order:', error);
      showSnackbar('Error resetting hotel order', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Order Hotels
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {hotels.map((hotel, index) => (
                <ListItem
                  key={hotel.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, index)}
                  sx={{
                    bgcolor: draggedItem === index ? '#e3f2fd' : 
                             dragOverIndex === index ? '#f3e5f5' : 'white',
                    borderBottom: '1px solid #e0e0e0',
                    transition: 'background-color 0.2s',
                    cursor: 'grab',
                    '&:hover': {
                      bgcolor: draggedItem === index ? '#e3f2fd' : '#f5f5f5'
                    },
                    '&:active': {
                      cursor: 'grabbing'
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mr: 2,
                      color: '#666'
                    }}
                  >
                    <GripVertical size={20} />
                  </Box>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ 
                          fontWeight: 'bold',
                          bgcolor: '#1976d2',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '12px',
                          minWidth: '24px',
                          textAlign: 'center'
                        }}>
                          {index + 1}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {hotel.name}
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveUp(index);
                      }}
                      disabled={index === 0}
                      sx={{ 
                        color: index === 0 ? '#ccc' : '#1976d2',
                        '&:hover': {
                          bgcolor: index === 0 ? 'transparent' : '#e3f2fd'
                        }
                      }}
                    >
                      <ArrowUp size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveDown(index);
                      }}
                      disabled={index === hotels.length - 1}
                      sx={{ 
                        color: index === hotels.length - 1 ? '#ccc' : '#1976d2',
                        '&:hover': {
                          bgcolor: index === hotels.length - 1 ? 'transparent' : '#e3f2fd'
                        }
                      }}
                    >
                      <ArrowDown size={16} />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        
        <Divider />
        
        {/* Botões de ação */}
        <Box sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <Button
            onClick={handleReset}
            startIcon={<RotateCcw size={16} />}
            disabled={saving || loading}
            fullWidth
            sx={{ 
              color: '#ff6b35',
              borderColor: '#ff6b35',
              backgroundColor: 'rgb(255, 255, 255)',
              border: '1px solid #ff6b35',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#ff6b35',
                color: '#fff',
              }
            }}
          >
            Reset
          </Button>
          
          <Button 
            onClick={onClose} 
            disabled={saving}
            fullWidth
            startIcon={<X size={16} />}
            sx={{ 
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
            disabled={saving || loading}
            fullWidth
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(to right, #1976d2, #0d47a1)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(to right, #1565c0, #003c8f)',
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HotelOrderModal;